import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoArrowBack,
  IoSearchOutline,
  IoCubeOutline,
  IoAlertCircleOutline,
  IoChatbubbleEllipsesOutline,
  IoClose,
} from "react-icons/io5";
import api from "@/api";
import { useI18n, STORE_LABELS } from "@/i18n";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { ListSkeleton } from "@/components/Skeletons";
import { tapLight } from "@/utils/haptics";

export default function GlobalSearchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ products: [], problems: [], chats: [] });
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const trimmed = debouncedQ.trim();
    if (!trimmed) {
      setResults({ products: [], problems: [], chats: [] });
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/search", { params: { q: trimmed, limit: 8 } });
        if (alive) setResults(data || { products: [], problems: [], chats: [] });
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [debouncedQ]);

  const totalHits = useMemo(
    () => (results.products?.length || 0) + (results.problems?.length || 0) + (results.chats?.length || 0),
    [results],
  );

  const goProduct = (p) => {
    tapLight();
    navigate(`/product/${p.id}`);
  };
  const goProblem = (p) => {
    tapLight();
    navigate(`/problem/${p.id}`);
  };
  const goChat = (c) => {
    tapLight();
    navigate(`/chat/${c.conv_id}`);
  };

  return (
    <div className="mobile-frame" data-testid="global-search-page">
      <div className="tf-search-header">
        <button
          data-testid="search-back"
          onClick={() => navigate(-1)}
          className="tf-icon-btn ghost"
          aria-label="Indietro"
        >
          <IoArrowBack size={22} />
        </button>
        <div className="tf-search-input-wrap">
          <IoSearchOutline size={18} className="tf-search-icon" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            className="tf-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search_everywhere_placeholder") || "Cerca prodotti, segnalazioni, messaggi…"}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {q && (
            <button
              type="button"
              data-testid="search-clear"
              className="tf-icon-btn ghost"
              onClick={() => { setQ(""); inputRef.current?.focus(); }}
              aria-label="Cancella"
              style={{ padding: 6 }}
            >
              <IoClose size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="tf-content">
        {loading ? (
          <ListSkeleton rows={5} />
        ) : !debouncedQ.trim() ? (
          <div className="tf-empty" data-testid="search-empty-initial">
            <IoSearchOutline size={44} />
            <div>Digita per cercare in tutta l&apos;app</div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
              Prodotti, EAN, Segnalazioni, Messaggi in chat
            </div>
          </div>
        ) : totalHits === 0 ? (
          <div className="tf-empty" data-testid="search-empty-noresults">
            <IoSearchOutline size={44} />
            <div>Nessun risultato per &ldquo;{debouncedQ}&rdquo;</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {results.products?.length > 0 && (
              <section data-testid="search-section-products" className="tf-search-section tf-search-section-products">
                <div className="tf-search-section-title tf-search-title-red">
                  <IoCubeOutline size={16} /> Prodotti ({results.products.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {results.products.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`search-product-${p.id}`}
                      className="tf-search-result tf-search-result-red"
                      onClick={() => goProduct(p)}
                    >
                      <div className="tf-search-result-title">{p.name || "—"}</div>
                      <div className="tf-search-result-sub">
                        {t(STORE_LABELS[p.store_id]) || p.store_id} · {p.quantity ?? 0} pz
                        {p.barcode ? ` · EAN ${p.barcode}` : ""}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {results.problems?.length > 0 && (
              <section data-testid="search-section-problems" className="tf-search-section">
                <div className="tf-search-section-title">
                  <IoAlertCircleOutline size={16} /> Segnalazioni ({results.problems.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {results.problems.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`search-problem-${p.id}`}
                      className="tf-search-result"
                      onClick={() => goProblem(p)}
                    >
                      <div className="tf-search-result-title">{p.title || "—"}</div>
                      <div className="tf-search-result-sub">
                        {t(STORE_LABELS[p.store_id]) || p.store_id} · {p.status || "open"} · {p.priority || "low"}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {results.chats?.length > 0 && (
              <section data-testid="search-section-chats" className="tf-search-section tf-search-section-chats">
                <div className="tf-search-section-title tf-search-title-yellow">
                  <IoChatbubbleEllipsesOutline size={16} /> Messaggi ({results.chats.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {results.chats.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`search-chat-${c.id}`}
                      className="tf-search-result tf-search-result-yellow"
                      onClick={() => goChat(c)}
                    >
                      <div className="tf-search-result-title">{c.text || "(allegato)"}</div>
                      <div className="tf-search-result-sub">
                        {c.sender_store === "titolare" ? "Titolare" : (t(STORE_LABELS[c.sender_store]) || c.sender_store)}
                        {" · "}
                        {new Date(c.created_at).toLocaleDateString("it-IT")}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
